import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useEffect,
  useState,
} from 'react';

import type { Token as Color } from '../../../../../export/brand0/colors';
import { Counter } from '../../counter';
import { Icon } from '../../icon';
import { cropText } from '../../lib/crop-text';
import { Typography } from '../../typography';
import { ItemsDisplayMode } from '../config';
import { useFloatingMenuContext } from '../context';

import styles from './item.scss';

type FloatingMenuItemProps = RefAttributes<HTMLButtonElement | HTMLLinkElement> &
  HTMLAttributes<HTMLButtonElement | HTMLLinkElement> & {
    label?: {
      text?: string;
      value?: string;
    };
    icon?: {
      name?: string;
      activeName?: string;
      asImage?: boolean;
    };
    order?: number;
    href?: string;
    active?: boolean;
    isActionButton?: boolean;
    counter?: {
      value?: number | string;
      className?: string;
      asNotificationBadge?: boolean;
      [key: string]: unknown;
    };
    labelColor?: string;
    valueColor?: string;
    iconFill?: Color;
    color?: string;
    radius?: number;
    align?: string;
    dataTestId?: string;
  };

const COMPONENT_ID = 'floating-action-button';
const MAX_LABEL_LENGTH = 20;

const DEFAULT_COUNTER = {} as {
  value?: number | string;
  className?: string;
  asNotificationBadge?: boolean;
  [K: string]: any;
};

export const FloatingMenuItem: ForwardRefExoticComponent<FloatingMenuItemProps> = forwardRef<
  HTMLButtonElement | HTMLLinkElement,
  FloatingMenuItemProps
>(
  (
    {
      label: { text: labelText = '', value: labelValue, ...labelProps } = {},
      icon: { name: iconToken = '', activeName: activeIconToken = '', asImage = false } = {},
      href,
      active,
      isActionButton,
      counter: {
        value: counterValue,
        className: counterClassName,
        asNotificationBadge: counterAsNotificationBadge,
        ...counterProps
      } = DEFAULT_COUNTER,
      labelColor,
      valueColor,
      iconFill,
      color,
      radius,
      align,
      order,
      dataTestId,
      ...rest
    },
    ref,
  ) => {
    const { itemsDisplayMode } = useFloatingMenuContext();

    const withIcon =
      (iconToken || (active && activeIconToken)) &&
      [ItemsDisplayMode.Icons, ItemsDisplayMode.IconsTitles].includes(itemsDisplayMode);

    const withLabel =
      !!labelText.length &&
      [ItemsDisplayMode.Titles, ItemsDisplayMode.IconsTitles].includes(itemsDisplayMode);

    const withCounter = counterValue !== undefined && counterValue !== null;

    const [iconName, setIconName] = useState('');

    const counterElement = withCounter && (
      <span
        data-testid={`${COMPONENT_ID}-counter`}
        className={classNames(styles.counter, styles[itemsDisplayMode], counterClassName)}
        {...counterProps}>
        <Counter
          value={!counterAsNotificationBadge ? counterValue : 0}
          className="caption-2-medium"
          notEmpty={!counterAsNotificationBadge}
        />
      </span>
    );

    const Tag = href ? 'a' : 'button';
    if (!withIcon && !withLabel) {
      return null;
    }
    useEffect(() => {
      if (!withIcon || asImage) return;
      const resolvedIconName = active
        ? activeIconToken || iconToken.replace('outlined', 'filled')
        : iconToken;
      setIconName(resolvedIconName);
    }, [active, withIcon, iconToken, activeIconToken, asImage]);

    const itemStyle = {
      order: `${order}`,
    };
    const buttonStyle = {
      backgroundColor: color && `var(${color})`,
      borderRadius: `${radius}px`,
    };
    const labelStyle = {
      color: `var(${labelColor})`,
    };
    const valueStyle = {
      color: `var(${valueColor})`,
    };

    return (
      <Tag
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ref={ref}
        {...(href && { href })}
        key={labelText}
        {...rest}
        data-testid={dataTestId}
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        style={itemStyle}
        className={classNames(styles['floating-menu-item'], {
          [styles.active]: active,
          [styles.isActionButton]: isActionButton,
          [styles.left]: align === 'left',
        })}>
        {withLabel && (
          <div className={classNames(styles['label-wrapper'])}>
            <Typography
              variant={labelValue ? 'caption-1-regular' : 'subhead-semibold'}
              className={styles.label}
              style={labelStyle}
              data-testid="item-label"
              {...labelProps}>
              {cropText(labelText, MAX_LABEL_LENGTH)}
            </Typography>
            {labelValue && (
              <Typography variant="subhead-semibold" style={valueStyle} data-testid="item-value">
                {labelValue}
              </Typography>
            )}
          </div>
        )}
        <div className={styles.image} style={buttonStyle} data-testid="item-icon">
          {withIcon && <Icon size={24} name={iconName} fill={iconFill} />}
        </div>
        {counterElement}
      </Tag>
    );
  },
);

FloatingMenuItem.displayName = 'FloatingMenuItem';
